// World Boss page (rules: shared/world-boss.ts): today's boss and when it changes, the server's damage climbing
// through its tiers, your team and attacks, the last attack's damage by creature, today's top attackers and
// yesterday's result. Until it opens (WB.open) it reads "Coming soon" and the Attack button is off (test accounts can try).
import { phoneSections, openSection } from './psec.ts';
import { WB, wbCoins, wbRevives, wbTier, type WbDTO, type WbResult, type WbRow } from '../shared/world-boss.ts';
import { SPECIES } from '../shared/species.ts';
import { canFight } from '../shared/xp.ts';
import { asFighter } from '../shared/social.ts';
import { ITEM, BOSS_TOKEN } from '../shared/items.ts';
import { BOSSES } from '../shared/species.ts';
import type { ArenaCreature } from '../shared/arena.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { esc, full, num, left, elChip, cname } from './fmt.ts';
import { bossPic, iconUrl, spriteUrl } from './assets.ts';
import { teamFace } from './arena.ts';
import { playerIcon } from './playericon.ts';
import { teamStrip } from './teampick.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { synergyChips } from './synergy.ts';
import { logLine } from './hud.ts';
import { bossBoardHtml, bossBattleOf } from './bossboard.ts';
import { afterReplay } from './boardview.ts';

export const W = {
  data: null as WbDTO | null,
  result: null as WbResult | null,
  /** The team picked on this page (null: last attack's team, else the hunt team). */
  ids: null as number[] | null,
  busy: false,
};

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindWorldBoss(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

export function loadWorldBoss() { send({ t: 'wb' }); }
export function onWb(d: WbDTO) { W.data = d; W.busy = false; rerender(); }
export function onWbResult(r: WbResult, d: WbDTO) {
  W.result = r; W.data = d; W.busy = false;
  openSection('worldboss', 'fight');
  logLine(`World Boss: ${full(r.dmg)} damage to ${esc(SPECIES[r.boss]?.name ?? 'the boss')}${r.newBest && r.counted ? ' · new best' : ''}`, r.newBest ? 'gold' : '');
  rerender();
}

/** A creature as it fights the boss: at Lv WB.level, up or down. */
const asBossFighter = (c: CreatureDTO): ArenaCreature => ({ ...asFighter(c), level: WB.level });
const fits = (c: CreatureDTO) => !c.listed && canFight(c, S.trainer!.level);

function teamIds(): number[] {
  const own = (ids: number[]) => ids.filter((id) => { const c = S.creatures.get(id); return !!c && fits(c); });
  if (W.ids) return own(W.ids);
  const last = W.data?.mine?.ids;
  if (last?.length) return own(last);
  // The hunt team, one of each species.
  const seen = new Set<string>();
  return own(S.trainer!.team).filter((id) => { const sp = S.creatures.get(id)!.species; if (seen.has(sp)) return false; seen.add(sp); return true; }).slice(0, WB.teamMax);
}

export function worldBossHead() {
  const d = W.data;
  return { meta: d?.open ? `New boss in ${left(d.endsAt - serverNow())}` : '', actions: '<button class="btn small" data-act="wb-refresh">Refresh</button>' };
}

export function worldBossPane(): string {
  const d = W.data;
  if (!d) return '<p class="hint">Loading the World Boss…</p>';
  return `<div class="wb">${phoneSections('worldboss', [
    { key: 'boss', title: 'Boss', html: `${heroHtml(d)}${serverHtml(d)}` },
    { key: 'attack', title: 'Attack', html: attackHtml(d) },
    { key: 'fight', title: 'Last attack', html: resultHtml() },
    { key: 'top', title: 'Top', html: `${topHtml(d.top, 'Today\'s top attacks', d)}${yesterdayHtml(d)}` },
  ])}</div>`;
}

function heroHtml(d: WbDTO) {
  const sp = SPECIES[d.boss];
  return `<section class="panel wb-hero">${d.open ? '' : '<span class="tn2-ribbon">Coming soon</span>'}
    <div class="wb-pic">${bossPic(d.boss)}</div>
    <div class="grow"><h3>${esc(sp?.name ?? d.boss)}</h3>
      <div class="chips">${(sp?.elements ?? []).map(elChip).join('')}<span class="pill">Lv ${WB.level}</span><span class="pill">Can't be defeated</span></div>
      ${d.open ? `<p class="muted">New boss in ${left(d.endsAt - serverNow())}</p>` : ''}</div></section>`;
}

/** The tier track: where the server's total sits among the tiers (log scale, so tier 1 isn't a sliver). */
function serverHtml(d: WbDTO) {
  const tiers = WB.tiers;
  const lo = Math.log10(tiers[0] / 3), hi = Math.log10(tiers[tiers.length - 1]);
  const at = (v: number) => Math.max(0, Math.min(100, ((Math.log10(Math.max(1, v)) - lo) / (hi - lo)) * 100));
  const marks = tiers.map((v, i) => `<span class="wb-mark${d.tier > i ? ' on' : ''}" style="left:${at(v)}%"><i></i><b>${i + 1}</b><em>${num(v)}</em></span>`).join('');
  const next = tiers[d.tier];
  return `<section class="panel wb-server"><div class="wb-row"><h4>Server total today</h4><span class="spacer"></span><b class="wb-total">${full(d.total)}</b></div>
    <div class="wb-track" role="img" aria-label="Tier ${d.tier} of ${tiers.length}"><div class="wb-fill" style="width:${at(d.total)}%"></div>${marks}</div>
    <p class="muted">${d.attackers} ${d.attackers === 1 ? 'trainer' : 'trainers'} · each counts up to ${full(WB.serverCap)} · tier ${d.tier} of ${tiers.length}${next ? ` · ${full(next - d.total)} to tier ${d.tier + 1}` : ''}${d.tier ? ` · everyone's coins +${Math.round(WB.tierBonus * d.tier * 100)}%` : ''}</p></section>`;
}

function attackHtml(d: WbDTO) {
  const t = S.trainer!;
  if (!d.unlocked) return `<section class="panel wb-attack"><p class="hint">Opens at trainer Lv ${WB.unlockLevel}</p></section>`;
  const ids = teamIds();
  const team = ids.map((id) => S.creatures.get(id)!).filter(Boolean);
  const used = d.mine?.tries ?? 0, leftN = WB.tries - used;
  const canTry = d.open || d.test;
  const btn = !canTry ? '<button class="btn primary" disabled>Coming soon</button>'
    : leftN <= 0 ? `<button class="btn" disabled>No attacks left · next boss in ${left(d.endsAt - serverNow())}</button>`
    : `<button class="btn primary" data-act="wb-attack"${team.length && !W.busy ? '' : ' disabled'}>${W.busy ? 'Attacking…' : `Attack · ${leftN} left`}</button>`;
  const best = d.mine?.best ?? 0;
  const tier = wbTier(d.total);
  const pay = best ? wbCoins(t.level, best, tier) : 0;
  const rv = wbRevives(tier);
  const mine = best ? `<div class="wb-mine"><span><i>Your best</i><b>${full(best)}</b></span>${d.mine!.rank ? `<span><i>Rank</i><b>#${d.mine!.rank}</b></span>` : ''}<span><i>If the day ended now</i><b><img src="${iconUrl('coin')}" alt="">${full(pay)}${rv ? ` <img src="${iconUrl('revive')}" alt="">×${rv}` : ''}</b></span></div>` : '';
  return `<section class="panel wb-attack">
    <div class="tn-sec"><h4>Your team <span class="muted">${team.length}/${WB.teamMax} · fights at Lv ${WB.level}</span></h4>${synergyChips(team, undefined, (i) => cname(team[i]))}<span class="spacer"></span><button class="btn small" data-act="wb-team">Change team</button></div>
    ${teamStrip(team.map(asBossFighter), WB.teamMax, 'wb-team')}
    ${mine}
    <div class="actions">${btn}${d.test ? '<span class="muted">Test account: attacks don\'t count.</span>' : ''}</div></section>`;
}

function resultHtml() {
  const r = W.result;
  if (!r || r.boss !== W.data?.boss) return '';
  const max = Math.max(1, ...Object.values(r.byId));
  const rows = r.team.map((c) => {
    const dmg = r.byId[c.id] ?? 0;
    const out = r.fell.indexOf(c.id);
    return `<li><span class="wb-face"><img src="${spriteUrl(c.species, c.aura)}" alt=""></span><span class="wb-name">${esc(SPECIES[c.species]?.name ?? c.species)}</span>
      <span class="wb-bar"><i style="width:${(dmg / max) * 100}%"></i></span><b>${full(dmg)}</b><span class="muted wb-fell">${out >= 0 ? `fell ${ordinal(out + 1)}` : 'standing'}</span></li>`;
  }).join('');
  const replay = r.log && r.bossMaxHp && r.teamMaxHp
    ? bossBoardHtml({ boss: r.boss, level: WB.level, teamLevel: WB.level, win: false, log: r.log, bossMaxHp: r.bossMaxHp, teamMaxHp: r.teamMaxHp, team: r.team }, r, { counter: true, end: `${full(r.dmg)} damage` })
    : '';
  // The damage waits for the replay's end (or Skip).
  // On a phone the board shrinks once the summary shows under it (public/fit.css).
  return `<section class="panel wb-result${r.newBest ? ' best' : ''}" style="--bv-room-after:${470 + r.team.length * 30}px">${replay}${afterReplay(bossBattleOf(r), `<div class="wb-row"><h4>Last attack</h4><span class="spacer"></span><b class="wb-dmg">${full(r.dmg)}</b></div>
    <p class="muted">${r.newBest ? (r.counted ? 'New best for today.' : 'Test attack, not counted.') : `Your best today stays ${full(r.best)}.`}${r.crits ? ` ${r.crits} critical ${r.crits === 1 ? 'hit' : 'hits'}.` : ''}</p>
    <ul class="wb-split">${rows}</ul>`)}</section>`;
}

const ordinal = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;

function topHtml(rows: WbRow[], title: string, d: WbDTO) {
  if (!rows.length) return `<section class="panel wb-top"><h4>${title}</h4><p class="hint">${d.open ? 'No attacks yet today.' : 'Coming soon.'}</p></section>`;
  return `<section class="panel wb-top"><h4>${title}</h4><table class="tbl2"><tr><th>#</th><th>Trainer</th><th>Team</th><th class="num">Damage</th></tr>
    ${rows.map((r) => `<tr class="${r.me ? 'on' : ''}"><td>${r.rank}${r.rank <= WB.keyPlaces && BOSS_TOKEN[d.boss] ? ` <img class="wb-key" src="${iconUrl(BOSS_TOKEN[d.boss])}" alt="" title="Top ${WB.keyPlaces} earn a boss key: today's, or the highest one their hunts drop">` : ''}</td><td class="hl-who">${playerIcon({ outfit: r.outfit, size: 'md' })}${esc(r.name)}</td><td><span class="hl-mini">${r.team.map((c) => teamFace(c, '', false, undefined, WB.level)).join('')}</span></td><td class="num">${full(r.best)}</td></tr>`).join('')}</table></section>`;
}

function yesterdayHtml(d: WbDTO) {
  const y = d.yesterday;
  if (!y) return '';
  const name = SPECIES[y.boss]?.name ?? y.boss;
  const mine = y.mine ? `<p>You dealt <b>${full(y.mine.best)}</b> (#${y.mine.rank}) and got ${full(y.mine.coins)} coins${y.mine.revives ? `, ${y.mine.revives} ${esc(ITEM.revive.name)}${y.mine.revives > 1 ? 's' : ''}` : ''}${y.mine.key ? `, <img class="ic" src="${iconUrl(y.mine.key)}" alt="">${esc(ITEM[y.mine.key]?.name ?? y.mine.key)}` : ''}.</p>` : '';
  return `<section class="panel wb-top wb-yday"><h4>Yesterday · ${esc(name)}</h4>
    <p class="muted">${y.attackers} ${y.attackers === 1 ? 'trainer' : 'trainers'} dealt ${full(y.total)} · tier ${y.tier}</p>${mine}
    ${y.top.length ? `<ol class="wb-podium">${y.top.map((r) => `<li class="${r.me ? 'on' : ''}">${playerIcon({ outfit: r.outfit, size: 'md' })}<b>${esc(r.name)}</b><span class="muted">${full(r.best)}</span></li>`).join('')}</ol>` : ''}</section>`;
}

/** Clicks on data-act="wb-…". */
export function worldBossClick(act: string): boolean {
  switch (act) {
    case 'wb-refresh': loadWorldBoss(); return true;
    case 'wb-attack': {
      const ids = teamIds();
      if (!ids.length || W.busy) return true;
      W.busy = true; send({ t: 'wbAttack', ids }); break;
    }
    case 'wb-team': {
      const lv = S.trainer!.level;
      openTeamBuilder({
        title: 'World Boss team', ids: teamIds(), max: WB.teamMax, unique: true, name: 'worldboss', key: String(lv),
        ok: fits, asFought: asBossFighter,
        onSave: (ids) => { W.ids = ids; rerender(); },
      });
      return true;
    }
    default: return false;
  }
  rerender();
  return true;
}

/** An error while attacking frees the button. */
export function worldBossErr() { if (W.busy) { W.busy = false; rerender(); } }

/** Help page section. Numbers come from shared/world-boss.ts. */
export function worldBossHelp(): string {
  return `<p>One boss a day for the whole server. It can't be defeated: the aim is to deal as much damage as you can. A new boss arrives at midnight UTC, the region bosses in turn.</p>
    <ul><li><b>Attacks</b>: from trainer Lv ${WB.unlockLevel}, ${WB.tries} attacks a day with up to ${WB.teamMax} creatures, one of each species. Your best attack of the day is your score.</li>
      <li><b>The fight</b>: the boss fight from the Bosses page. Your team attacks until it is knocked out or ${3} minutes pass; the boss hits your first creature that is still standing. Every creature and the boss fight at Lv ${WB.level}, up or down, so species, rolls, stars, relics, element counters and team order decide the damage, not levels. The Hall Keeper bonus counts.</li>
      <li><b>Server tiers</b>: everyone's best attacks add up, each counting up to ${full(WB.serverCap)}. The total climbs through ${WB.tiers.length} tiers (${WB.tiers.map((v) => full(v)).join(', ')}); every tier reached adds ${Math.round(WB.tierBonus * 100)}% to everyone's coins.</li>
      <li><b>Rewards</b>, when the day ends, for everyone who attacked: coins, one hunt kill's coins at your trainer level for every ${WB.coinPer} damage of your best (at most ${WB.coinKillsMax} kills), times the tier bonus. From tier ${WB.revives[0][0]} everyone also gets ${WB.revives[0][1]} Revive, from tier ${WB.revives[1][0]} ${WB.revives[1][1]} Revives. The day's top ${WB.keyPlaces} attacks each earn a key to that day's boss (${BOSSES.map((b) => esc(ITEM[BOSS_TOKEN[b.id]].name)).join(', ')}), or, if your hunts don't drop that key yet, the highest key they do.</li>
      <li><b>Coming soon</b>: the World Boss opens once there are enough trainers to make the server tiers mean something. Admin and playtest accounts can attack to test it; their attacks never count.</li></ul>`;
}
